import { useState, useEffect } from 'react';
import { Users, GraduationCap, BookOpen, FileText, ChevronDown, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, Legend } from 'recharts';
import api from '../../services/api';

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];
const MONTHS = [
  { value: '', label: 'All Months' },
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Feb' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' },
  { value: 5, label: 'May' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Aug' },
  { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Dec' },
];

function StatCard({ title, value, icon: Icon, color = 'indigo', subtitle }) {
  const colorClasses = {
    indigo: 'from-indigo-500 to-indigo-600',
    purple: 'from-purple-500 to-purple-600',
    pink: 'from-pink-500 to-pink-600',
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm text-gray-500 font-medium truncate">{title}</p>
          <p className="text-xl sm:text-3xl font-bold text-gray-800 mt-1 sm:mt-2">{value}</p>
          {subtitle && <p className="text-xs sm:text-sm text-gray-400 mt-1 truncate">{subtitle}</p>}
        </div>
        <div className={`p-2 sm:p-3 rounded-xl bg-gradient-to-br text-white flex-shrink-0 ${colorClasses[color]}`}>
          <Icon className="w-4 h-4 sm:w-6 sm:h-6" />
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({ total_students: 0, total_lecturers: 0, total_exams: 0, total_grades: 0 });
  const [loading, setLoading] = useState(true);
  const [recentStudents, setRecentStudents] = useState([]);
  const [gradeDistribution, setGradeDistribution] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);

  // Filters and additional data
  const [grades, setGrades] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [attendanceGranularity, setAttendanceGranularity] = useState('month');
  const [topStudents, setTopStudents] = useState([]);
  const [leastStudents, setLeastStudents] = useState([]);
  const [subjectExamPerf, setSubjectExamPerf] = useState([]);
  const [examAttendance, setExamAttendance] = useState([]);

  useEffect(() => { loadGrades(); }, []);
  useEffect(() => { loadDashboardData(); }, [selectedGrade, selectedMonth, attendanceGranularity]);

  const loadGrades = async () => {
    try {
      const res = await api.get('/admin/grades');
      if (res.data.success) setGrades(res.data.grades || []);
    } catch (err) {
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedGrade) params.grade_id = selectedGrade;
      if (selectedMonth) params.month = selectedMonth;
      if (attendanceGranularity) params.attendance_granularity = attendanceGranularity;

      const [dashRes, studentsRes] = await Promise.all([
        api.get('/admin/dashboard', { params }).catch(err => {
          return { data: { success: false, dashboard: {} } };
        }),
        api.get('/admin/students').catch(err => {
          return { data: { success: false, students: [] } };
        }),
      ]);

      if (dashRes.data.success && dashRes.data.dashboard) {
        const dash = dashRes.data.dashboard;
        setStats(dash);
        
        // Set real grade distribution data
        if (Array.isArray(dash.grade_distribution)) {
          setGradeDistribution(dash.grade_distribution.map((g, i) => ({
            name: g.grade_name || 'Unknown', 
            value: g.student_count || 0,
            color: COLORS[i % COLORS.length]
          })));
        }
        
        // Set real attendance trend data
        if (Array.isArray(dash.attendance_trend) && dash.attendance_trend.length > 0) {
          setAttendanceData(dash.attendance_trend);
        }

        // Top/Least students and subject performance
        setTopStudents(Array.isArray(dash.top_students) ? dash.top_students : []);
        setLeastStudents(Array.isArray(dash.least_students) ? dash.least_students : []);
        setSubjectExamPerf(Array.isArray(dash.subject_exam_performance) ? dash.subject_exam_performance : []);
        setExamAttendance(Array.isArray(dash.exam_attendance) ? dash.exam_attendance : []);
        // grade_performance is stored in stats and used directly for the combined grade chart
      }
      
      if (studentsRes.data.success && Array.isArray(studentsRes.data.students)) {
        setRecentStudents(studentsRes.data.students.slice(0, 5));
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // If stats data is missing or API failed, show a friendly error
  if (!stats || typeof stats !== 'object' || !('total_students' in stats)) {
    return <div className="flex items-center justify-center h-64 text-red-500 font-semibold">Failed to load dashboard data. Please try again later.</div>;
  }

  return (
    <div className="space-y-6 px-2 sm:px-0">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Admin Dashboard</h1>
        <p className="text-sm sm:text-base text-gray-500">English Literature Academic Management System</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <StatCard title="Total Students" value={stats.total_students} icon={Users} color="indigo" subtitle="Registered students" />
        <StatCard title="Total Lecturers" value={stats.total_lecturers} icon={GraduationCap} color="purple" subtitle="Active lecturers" />
        <StatCard title="Total Exams" value={stats.total_exams} icon={FileText} color="pink" subtitle="Created exams" />
        <StatCard title="Grades/Years" value={stats.total_grades} icon={BookOpen} color="blue" subtitle="Academic grades" />
      </div>

      {/* Filters Row (grade + month) */}
      {/* Filters Row (grade + month + attendance granularity) - moved below stats */}
      <div className="flex flex-wrap gap-4 items-center mb-4 mt-3">
        <div className="flex flex-col">
          <label className="block text-xs font-semibold text-gray-500 mb-1">Grade</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-500"><BookOpen className="w-4 h-4" /></span>
            <select
              className="appearance-none pl-10 pr-8 py-2 border border-indigo-200 rounded-xl shadow focus:ring-2 focus:ring-indigo-400 bg-white text-gray-800 font-medium min-w-[140px] transition-all hover:border-indigo-400 focus:border-indigo-500"
              value={selectedGrade}
              onChange={e => setSelectedGrade(e.target.value)}
            >
              <option value="">All Grades</option>
              {grades.map(g => (
                <option key={g.grade_id} value={g.grade_id}>{g.grade_name}</option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><ChevronDown className="w-4 h-4" /></span>
          </div>
        </div>

        <div className="flex flex-col">
          <label className="block text-xs font-semibold text-gray-500 mb-1">Month</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-pink-500"><Calendar className="w-4 h-4" /></span>
            <select
              className="appearance-none pl-10 pr-8 py-2 border border-pink-200 rounded-xl shadow focus:ring-2 focus:ring-pink-400 bg-white text-gray-800 font-medium min-w-[140px] transition-all hover:border-pink-400 focus:border-pink-500"
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
            >
              {MONTHS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"><ChevronDown className="w-4 h-4" /></span>
          </div>
        </div>


      </div>

      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Attendance Trend</h3>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">Granularity</label>
              <select
                className="appearance-none pl-2 pr-4 py-1 border border-gray-200 rounded-xl shadow bg-white text-gray-800 text-sm"
                value={attendanceGranularity}
                onChange={e => setAttendanceGranularity(e.target.value)}
              >
                <option value="day">Last 30 days</option>
                <option value="month">Last 6 months</option>
                <option value="year">Last 5 years</option>
              </select>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={attendanceData}>
              <defs>
                <linearGradient id="colorAtt" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" domain={[0, 100]} />
              <Tooltip formatter={(v) => [`${v}%`, 'Attendance']} />
              <Area type="monotone" dataKey="attendance" stroke="#6366f1" fill="url(#colorAtt)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Students by Grade</h3>
          {gradeDistribution.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={gradeDistribution} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value">
                    {gradeDistribution.map((entry, index) => (<Cell key={index} fill={entry.color} />))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-4">
                {gradeDistribution.map((g, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: g.color }}></div>
                    <span className="text-sm text-gray-600">{g.name}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400">No grades configured</div>
          )}
        </div>
      </div>

      {/* NOTE: Top/Least Students moved below Subject × Exam Performance per request */}

      {/* Exams x Attendance (replaces Subject × Exam) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Exams × Attendance</h3>
        {examAttendance.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={examAttendance} margin={{ left: -20, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="exam_label" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" domain={[0, 100]} />
              <Tooltip formatter={(v,name) => [`${v}%`, name]} />
              <Legend />
              <Line type="monotone" dataKey="avg_marks" name="Avg Marks" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="avg_attendance" name="Avg Attendance" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-40 flex items-center justify-center text-gray-400">No exam attendance data</div>
        )}
      </div>

      {/* Grade performance: separate card with one line per grade */}
      {Array.isArray(stats.grade_performance) && stats.grade_performance.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mt-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Grade Performance (All Grades)</h3>
          <ResponsiveContainer width="100%" height={300}>
            {/* Build a common x-axis across all exams */}
            <LineChart data={(() => {
              const grades = stats.grade_performance || [];
              // Collect all exams with dates
              const labelMap = new Map();
              grades.forEach(g => {
                (g.exams || []).forEach(ex => {
                  const key = ex.exam_date || ex.exam_label || ex.exam_name;
                  if (!labelMap.has(key)) {
                    labelMap.set(key, { label: ex.exam_label || ex.exam_name, date: ex.exam_date });
                  }
                });
              });
              // Sort by date
              const labels = Array.from(labelMap.entries()).sort((a,b) => {
                const da = a[1].date || '';
                const db = b[1].date || '';
                return da.localeCompare(db);
              }).map(x => x[0]);

              const data = labels.map(key => ({ label: labelMap.get(key).label }));
              grades.forEach(g => {
                const gradeKey = `grade_${g.grade_id}`;
                (g.exams || []).forEach(ex => {
                  const idx = labels.indexOf(ex.exam_date || ex.exam_label || ex.exam_name);
                  if (idx >= 0) data[idx][gradeKey] = ex.avg_marks;
                });
              });
              return data;
            })()}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" domain={[0, 100]} />
              <Tooltip />
              <Legend />
              {/* render lines for each grade */}
              {stats.grade_performance.map((g, i) => (
                <Line key={g.grade_id} type="monotone" dataKey={`grade_${g.grade_id}`} name={g.grade_name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      
      {/* Re-introduce Top/Least Performing Students below Subject performance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Top Performing Students</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Grade</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Avg Marks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {topStudents.length > 0 ? topStudents.map((s, i) => (
                  <tr key={i} className="hover:bg-green-50">
                    <td className="px-4 py-2 font-medium text-gray-800">{s.full_name}</td>
                    <td className="px-4 py-2">{s.grade_name}</td>
                    <td className="px-4 py-2">{s.avg_marks}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="3" className="px-4 py-8 text-center text-gray-400">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">Least Performing Students</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Grade</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Avg Marks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {leastStudents.length > 0 ? leastStudents.map((s, i) => (
                  <tr key={i} className="hover:bg-red-50">
                    <td className="px-4 py-2 font-medium text-gray-800">{s.full_name}</td>
                    <td className="px-4 py-2">{s.grade_name}</td>
                    <td className="px-4 py-2">{s.avg_marks}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="3" className="px-4 py-8 text-center text-gray-400">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-3 sm:p-4 border-b border-gray-100">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800">Recent Students</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Name</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase hidden sm:table-cell">Email</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Grade</th>
                <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recentStudents.length > 0 ? recentStudents.map((s, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-2 sm:px-4 py-2 sm:py-3 font-medium text-xs sm:text-sm text-gray-800 truncate max-w-[120px] sm:max-w-none">{s.full_name}</td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-600 hidden sm:table-cell">{s.email}</td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3"><span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] sm:text-xs">{s.grade_name || '-'}</span></td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3"><span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs ${Number(s.is_active) === 1 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>{Number(s.is_active) === 1 ? 'Active' : 'Inactive'}</span></td>
                </tr>
              )) : (
                <tr><td colSpan="4" className="px-4 py-8 text-center text-gray-400">No students yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>


      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6">
        <a href="/admin/students" className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-4 sm:p-6 text-white hover:shadow-lg transition">
          <Users className="w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-3" />
          <h3 className="text-base sm:text-lg font-semibold">Manage Students</h3>
          <p className="text-indigo-100 text-xs sm:text-sm mt-1">Add, edit, or remove students</p>
        </a>
        <a href="/admin/lecturers" className="bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl p-4 sm:p-6 text-white hover:shadow-lg transition">
          <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8 mb-2 sm:mb-3" />
          <h3 className="text-base sm:text-lg font-semibold">Manage Lecturers</h3>
          <p className="text-purple-100 text-xs sm:text-sm mt-1">Add lecturers and assign subjects</p>
        </a>
        <a href="/admin/subjects" className="bg-gradient-to-r from-pink-500 to-orange-500 rounded-xl p-6 text-white hover:shadow-lg transition">
          <BookOpen className="w-8 h-8 mb-3" />
          <h3 className="text-lg font-semibold">Manage Subjects</h3>
          <p className="text-pink-100 text-sm mt-1">Configure subjects and grades</p>
        </a>
      </div>
    </div>
  );
}
